<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { cn } from '@/utils/utils'
import { useSidebar } from './utils'
import {
  SIDEBAR_COLLAPSE_THRESHOLD_PX,
  SIDEBAR_DEFAULT_WIDTH_PX,
  SIDEBAR_MIN_WIDTH_PX,
} from '@/stores/sidebarStore'

/**
 * The `SidebarRail` is both a click-to-toggle target and a drag-to-resize
 * handle. The two interactions disambiguate by movement: a pointer that
 * moves less than `CLICK_DRAG_THRESHOLD_PX` between down and up is treated
 * as a click and falls through to the legacy `toggleSidebar()` behaviour;
 * anything more is a resize, and the click-toggle is suppressed.
 *
 * While dragging we drive the live width through `setWidth()` (clamped to
 * the store's min/max). If the user drags below
 * `SIDEBAR_COLLAPSE_THRESHOLD_PX` we collapse to icon mode; if they drag
 * back past that threshold from a collapsed state we expand and snap back
 * to the persisted width as a starting point. This makes the rail behave
 * like the resize handle on common IDE / file-browser sidebars (VS Code,
 * Finder), where the same gesture both sizes and (optionally) collapses.
 */

const props = defineProps<{
  class?: HTMLAttributes['class']
}>()

const {
  toggleSidebar,
  width,
  setWidth,
  setOpen,
  setResizing,
  state,
} = useSidebar()
// The rail reads `side` off the nearest `[data-side]` ancestor at drag
// time so the same component works for both `side="left"` and
// `side="right"` mounts (the sidebar itself owns that attribute).

const CLICK_DRAG_THRESHOLD_PX = 4

let pointerId: number | null = null
let startX = 0
let startWidth = 0
let didDrag = false
let resolvedSide: 'left' | 'right' = 'left'

function readSide(target: HTMLElement | null): 'left' | 'right' {
  const sidebar = target?.closest<HTMLElement>('[data-side]')
  return sidebar?.dataset.side === 'right' ? 'right' : 'left'
}

function onPointerDown(event: PointerEvent) {
  // Mouse left button only — let the browser handle middle/right clicks
  // (and let touch / pen continue to work via `pointerType !== 'mouse'`).
  if (event.pointerType === 'mouse' && event.button !== 0) {
    return
  }
  const target = event.currentTarget as HTMLElement
  pointerId = event.pointerId
  startX = event.clientX
  // Drag from the *currently rendered* width. When collapsed (icon mode),
  // any positive drag past the threshold opens the sidebar and we use the
  // persisted width as the initial expanded value.
  startWidth = state.value === 'collapsed' ? SIDEBAR_DEFAULT_WIDTH_PX : width.value
  didDrag = false
  resolvedSide = readSide(target)
  target.setPointerCapture(event.pointerId)
}

function onPointerMove(event: PointerEvent) {
  if (pointerId === null || event.pointerId !== pointerId) {
    return
  }
  const rawDelta = event.clientX - startX
  // Right-anchored rails grow when the cursor moves *left*, so flip the sign.
  const signedDelta = resolvedSide === 'right' ? -rawDelta : rawDelta
  if (!didDrag && Math.abs(rawDelta) >= CLICK_DRAG_THRESHOLD_PX) {
    didDrag = true
    setResizing(true)
  }
  if (!didDrag) {
    return
  }
  const projected = startWidth + signedDelta
  if (projected < SIDEBAR_COLLAPSE_THRESHOLD_PX) {
    // User dragged inward past the collapse line: snap to icon mode and
    // freeze the persisted width at the floor so reopening returns to a
    // sane size. We *don't* end the drag — the user can still drag back
    // out to expand without releasing.
    if (state.value !== 'collapsed') {
      setOpen(false)
    }
    setWidth(SIDEBAR_MIN_WIDTH_PX)
    return
  }
  if (state.value === 'collapsed') {
    // Crossed back over the threshold while collapsed → expand again.
    setOpen(true)
  }
  setWidth(projected)
}

function endDrag(event: PointerEvent) {
  if (pointerId === null || event.pointerId !== pointerId) {
    return
  }
  const target = event.currentTarget as HTMLElement
  if (target.hasPointerCapture(event.pointerId)) {
    target.releasePointerCapture(event.pointerId)
  }
  const wasDrag = didDrag
  pointerId = null
  startX = 0
  startWidth = 0
  didDrag = false
  setResizing(false)
  // Treat as a click when no measurable movement occurred. Without this
  // the legacy "click to toggle" affordance would silently disappear.
  if (!wasDrag) {
    toggleSidebar()
  }
}
</script>

<template>
  <button
    data-sidebar="rail"
    data-slot="sidebar-rail"
    aria-label="Resize Sidebar"
    title="Drag to resize, click to toggle"
    type="button"
    :tabindex="-1"
    :class="cn(
      'hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 cursor-ew-resize transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex touch-none select-none',
      'hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full',
      '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
      '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
      // Highlight the hairline while the rail is actively being dragged so
      // the user has a visible affordance during resize.
      'group-data-[resizing]/sidebar-wrapper:after:bg-sidebar-border',
      props.class,
    )"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="endDrag"
    @pointercancel="endDrag"
  >
    <slot />
  </button>
</template>
